import { Dialog, Transition } from "@headlessui/react";
import {
  BeakerIcon,
  BookOpenIcon,
  ChatAlt2Icon,
  ChatIcon,
  ChipIcon,
  LightBulbIcon,
  MenuAlt2Icon,
  PresentationChartLineIcon,
  XIcon
} from "@heroicons/react/solid";
import { noop } from "lodash";
import Tooltip from "rc-tooltip";
import React, { Fragment, useMemo, useState } from "react";
import { useAuthentication } from "../context/authentication";

interface Props {
  className?: string;
}

const HelpButton: React.FC<Props> = ({ className }) => {
  const { user, openChat } = useAuthentication();
  const [open, setOpen] = useState(false);

  const resourceItems: Item[] = useMemo(
    () => [
      {
        name: "Get started",
        url: "https://docs.hotspotty.net/getting-started/hotspotty-quickstart",
        Icon: MenuAlt2Icon
      },
      {
        name: "Docs",
        url: "https://docs.hotspotty.net",
        Icon: BookOpenIcon
      },
      {
        name: "Demo",
        url: "https://hotspotty.net/request-a-demo/",
        Icon: PresentationChartLineIcon
      },
      {
        name: "Community",
        url: "https://discord.gg/498Rc8khaN",
        Icon: ChatAlt2Icon
      },
      {
        name: "Contact us",
        onClick: () => {
          openChat();

          setOpen(false);
        },
        Icon: ChatIcon
      }
    ],
    [openChat]
  );

  const feedbackItems: Item[] = [
    {
      name: "Feature request",
      url: "https://feedback.hotspotty.net/feature-requests",
      Icon: LightBulbIcon
    },
    {
      name: "Feature improvement",
      url: "https://feedback.hotspotty.net/feature-improvement",
      Icon: BeakerIcon
    },
    {
      name: "Submit a bug",
      url: "https://feedback.hotspotty.net/bug-reports",
      Icon: ChipIcon
    }
  ];

  return (
    <div className={className}>
      <Tooltip placement="bottom" overlay="Help center">
        <button
          type="button"
          className="h-12 ml-2 mr-4 text-2xl inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none dark:text-gray-200 dark:hover:text-white"
          onClick={() => setOpen(true)}
        >
          ?
        </button>
      </Tooltip>

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed z-50 inset-0 overflow-y-auto"
          open={open}
          onClose={setOpen}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6 dark:bg-gray-800">
                <div className="block absolute top-10 left-10">
                  <img
                    src="/favicon-32x32.png"
                    width="32"
                    height="32"
                    alt="Hotspotty logo"
                  />
                </div>

                <div className="block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-transparent dark:text-gray-200 dark:hover:text-white"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="flex-shrink-0 flex flex-col p-4 mt-12">
                  <Dialog.Title
                    as="h2"
                    className="text-2xl font-medium text-gray-900 mb-2 dark:text-gray-200"
                  >
                    Hello {user?.name}
                  </Dialog.Title>

                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Let's build the Helium network together!
                  </p>

                  <h2 className="mt-6 text-lg font-medium text-gray-900 dark:text-gray-200">
                    Resources
                  </h2>

                  <dl className="mt-6 space-y-0 grid grid-cols-2 sm:grid-cols-3 gap-y-6 gap-x-6">
                    {resourceItems.map(renderItem)}
                  </dl>

                  <h2 className="mt-6 text-lg font-medium text-gray-900 dark:text-gray-200">
                    Feedback
                  </h2>

                  <dl className="mt-6 space-y-0 grid grid-cols-2 sm:grid-cols-3 gap-y-6 gap-x-6">
                    {feedbackItems.map(renderItem)}
                  </dl>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default HelpButton;

//
// Utils
//

interface Item {
  name: string;
  url?: string;
  onClick?: () => void;
  Icon: React.ComponentType<any>;
}

const renderItem = ({ name, url, onClick = noop, Icon }: Item) => {
  const renderContent = () => (
    <>
      <dt>
        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-hotspotty-400 text-white group-hover:bg-hotspotty-400">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </div>
      </dt>
      <dd className="mt-2 text-base text-gray-500 dark:text-gray-300 dark:group-hover:text-white">
        {name}
      </dd>
    </>
  );

  return url ? (
    <a
      key={name}
      className="group cursor-pointer"
      onClick={onClick}
      href={url}
      target="_blank"
      rel="noreferrer"
    >
      {renderContent()}
    </a>
  ) : (
    <div key={name} className="group cursor-pointer" onClick={onClick}>
      {renderContent()}
    </div>
  );
};
